// hash-deeplink.js: Reliable deep linking to in-page sections after loader/fonts & GSAP pins.
// Handles initial load with #hash and subsequent hash changes. Safari-friendly with retries.

(function(){
  // Configuration & dynamic environment sensing
  const MAX_ATTEMPTS_PHASE1 = 14;        // immediate retry passes (slightly higher)
  const MAX_ATTEMPTS_PHASE2 = 16;        // secondary passes
  const ATTEMPT_INTERVAL = 140;          // ms between retries (tighter cadence)
  const POST_LOAD_DELAY = 50;            // base delay after loader hidden
  const SECOND_PHASE_DELAY = 800;        // schedule a second pass
  const FONTS_READY_DELAY = 1500;        // third pass after fonts (extra Safari safety)
  const LATE_FORCED_DELAY = 2600;        // unconditional forced scroll if still at top
  const INTERVAL_SCAN_LIMIT = 24;        // number of interval scans before giving up
  const INTERVAL_SCAN_PERIOD = 250;      // ms between interval scans
  const MUTATION_OBSERVER_TIMEOUT = 3000;// fallback observer total window
  const REFRESH_PHASE_MAX = 6;           // attempts triggered by ScrollTrigger refreshEnd
  const MULTISHOT_SCROLL_COUNT = 3;      // send multiple scroll commands to overcome race
  const MULTISHOT_SCROLL_SPREAD = 60;    // ms spacing between multi shots
  const LENIS_MAX_WAIT = 1600;           // max ms to wait for Lenis before fallback
  const LENIS_POLL_INTERVAL = 120;       // poll interval for Lenis availability

  const debug = /[?&]debugHash=1/i.test(window.location.search);
  const log = (...args) => { if (debug) console.log('[hash-deeplink]', ...args); };
  const warn = (...args) => { if (debug) console.warn('[hash-deeplink]', ...args); };

  // Compute nav offset dynamically (first fixed header/nav element height)
  function computeOffset(){
    let offset = 0;
    const candidates = document.querySelectorAll('header, .site-header, nav, .main-nav, .global-nav');
    candidates.forEach(el => {
      const style = window.getComputedStyle(el);
      const isFixed = style.position === 'fixed' || style.position === 'sticky';
      if (isFixed) {
        const h = el.getBoundingClientRect().height;
        if (h > offset) offset = h; // take tallest fixed header
      }
    });
    return offset;
  }
  let dynamicOffsetCache = 0;
  function getOffset(){
    // recompute after fonts/layout settle; cheap so we can do more than once
    dynamicOffsetCache = computeOffset();
    return dynamicOffsetCache;
  }

  function getTargetFromHash() {
    const raw = window.location.hash.replace(/^#/, '').trim();
    if (!raw) return null;
    // IDs in markup may be percent-encoded; decode
    const id = decodeURIComponent(raw);
    const el = document.getElementById(id);
    if (!el) log('Target not found for id:', id);
    return el || null;
  }

  // Wait for Lenis (smooth scroll) before falling back; returns a promise
  function ensureLenis() {
    if (window.lenis && typeof window.lenis.scrollTo === 'function') return Promise.resolve(true);
    return new Promise(resolve => {
      const started = Date.now();
      const poll = () => {
        if (window.lenis && typeof window.lenis.scrollTo === 'function') {
          log('Lenis detected for deep link scroll');
          return resolve(true);
        }
        if (Date.now() - started >= LENIS_MAX_WAIT) {
          warn('Lenis not available within wait window; using window.scrollTo fallback');
          return resolve(false);
        }
        setTimeout(poll, LENIS_POLL_INTERVAL);
      };
      poll();
    });
  }

  function performScroll(el, behavior='auto') {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const absoluteTop = rect.top + window.pageYOffset - getOffset();
    const useSmooth = behavior === 'smooth';
    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      window.lenis.scrollTo(absoluteTop, { immediate: !useSmooth, lock: false });
      return true;
    }
    window.scrollTo({ top: absoluteTop, behavior: useSmooth ? 'smooth' : 'auto' });
    return true;
  }

  function multishotScroll(el){
    for (let i=0;i<MULTISHOT_SCROLL_COUNT;i++) {
      setTimeout(()=>{ performScroll(el,'auto'); }, i*MULTISHOT_SCROLL_SPREAD);
    }
  }

  // Minimum height required for scroll target to be considered visible
  const MIN_TARGET_HEIGHT = 80;
  function attemptHashScroll(attempt=1, phase='p1', maxAttempts=MAX_ATTEMPTS_PHASE1) {
    const el = getTargetFromHash();
    if (!el) { log('No element for hash on attempt', attempt, 'phase', phase); return false; }
    const bodyReady = document.body && !document.body.classList.contains('scroll-lock');
    const stRefreshing = (window.ScrollTrigger ? window.ScrollTrigger.isRefreshing : false);
    const stReady = !stRefreshing;
    const elHeight = el.getBoundingClientRect().height;
    const criteriaMet = bodyReady && stReady && elHeight >= MIN_TARGET_HEIGHT;
    log('Attempt', attempt, 'phase', phase, 'bodyReady=', bodyReady, 'stReady=', stReady, 'height=', elHeight, 'criteriaMet=', criteriaMet, 'offset=', dynamicOffsetCache);
    if (!criteriaMet && elHeight > 0 && elHeight < MIN_TARGET_HEIGHT) {
      warn('Target present but height too small (', elHeight, 'px); waiting for reveal/animation.');
    }
    if (criteriaMet) {
      // Ensure Lenis readiness before multi-shot sequence to keep all scrolls consistent
      ensureLenis().then(()=>{ multishotScroll(el); });
      return true;
    }
    if (attempt < maxAttempts) {
      setTimeout(()=>attemptHashScroll(attempt+1, phase, maxAttempts), ATTEMPT_INTERVAL);
    } else {
      warn('Phase', phase, 'exhausted attempts without criteria.');
    }
    return false;
  }

  // Interval scanning until success or limit reached (wait for minimum height)
  function intervalScan() {
    if (!window.location.hash) return;
    let scans = 0;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const handle = setInterval(()=>{
      scans++;
      const el = getTargetFromHash();
      if (el) {
        const h = el.getBoundingClientRect().height;
        if (h >= MIN_TARGET_HEIGHT) {
          const ok = attemptHashScroll(1,'scan',1); // single criteria check; multishot inside
          if (ok) {
            log('Interval scan success on scan', scans, 'for', id, 'height', h);
            clearInterval(handle);
          }
        } else if (h > 0) {
          warn('Interval scan: target height', h, 'px still below threshold.');
        }
      }
      if (scans >= INTERVAL_SCAN_LIMIT) {
        clearInterval(handle);
        warn('Interval scan limit reached without success');
      }
    }, INTERVAL_SCAN_PERIOD);
  }

  // Trigger after loader hidden OR immediate if loader already gone
  function scheduleInitial() {
    if (!window.location.hash) return;
    log('Scheduling initial deep link attempts for hash', window.location.hash);
    getOffset(); // initial offset measurement
    setTimeout(()=>attemptHashScroll(1,'p1',MAX_ATTEMPTS_PHASE1), POST_LOAD_DELAY);
    setTimeout(()=>{ getOffset(); attemptHashScroll(1,'p2',MAX_ATTEMPTS_PHASE2); }, SECOND_PHASE_DELAY);
    setTimeout(()=>{ getOffset(); attemptHashScroll(1,'p3',MAX_ATTEMPTS_PHASE2); }, FONTS_READY_DELAY);
    // Very late forced attempt irrespective of criteria (if still near top)
    setTimeout(()=>{
      if (window.pageYOffset < 40) {
        const el = getTargetFromHash();
        if (el) { warn('Late forced scroll fired'); multishotScroll(el); }
      }
    }, LATE_FORCED_DELAY);
    // Start interval scanning
    intervalScan();
    // Mutation observer fallback: watch for target height changes (wait for minimum height)
    const target = getTargetFromHash();
    if (target) {
      let observedInitial = target.getBoundingClientRect().height;
      const mo = new MutationObserver(()=>{
        const h = target.getBoundingClientRect().height;
        if (h !== observedInitial && h >= MIN_TARGET_HEIGHT) {
          log('Mutation observed height change; attempting scroll. Height:', h);
          attemptHashScroll(1,'mo',MAX_ATTEMPTS_PHASE1);
        } else if (h > 0 && h < MIN_TARGET_HEIGHT) {
          warn('Mutation observed height change but still below threshold:', h);
        }
      });
      try { mo.observe(target, { attributes:true, childList:true, subtree:true }); } catch(e) {}
      setTimeout(()=>{ mo.disconnect(); }, MUTATION_OBSERVER_TIMEOUT);
    }
  }

  if (window.__loaderHiddenFired) {
    scheduleInitial();
  } else {
    window.addEventListener('app:loaderHidden', scheduleInitial, { once:true });
  }

  // Wait for both DOMContentLoaded and app:gsapReady before running hash scroll attempts.
  let domReady = false;
  let gsapReady = false;
  function tryRunAfterAllReady() {
    if (!domReady || !gsapReady) return;
    if (!window.location.hash) return;
    log('All ready: DOM and GSAP/ScrollTrigger. Running deep link scroll.');
    getOffset();
    attemptHashScroll(1,'allReady',MAX_ATTEMPTS_PHASE1);
  }
  document.addEventListener('DOMContentLoaded', () => {
    domReady = true;
    tryRunAfterAllReady();
  });
  window.addEventListener('app:gsapReady', () => {
    gsapReady = true;
    tryRunAfterAllReady();
  });

  // ScrollTrigger refresh-end hook attempts (if available)
  if (window.ScrollTrigger) {
    let refreshAttempts = 0;
    window.ScrollTrigger.addEventListener('refreshEnd', () => {
      if (!window.location.hash) return;
      if (refreshAttempts >= REFRESH_PHASE_MAX) return;
      refreshAttempts++;
      log('ScrollTrigger refreshEnd attempt', refreshAttempts);
      getOffset();
      attemptHashScroll(1,'refresh'+refreshAttempts,MAX_ATTEMPTS_PHASE1);
    });
  }

  // Support dynamic hash changes
  window.addEventListener('hashchange', () => {
    log('hashchange detected', window.location.hash);
    getOffset();
    attemptHashScroll(1,'hash',MAX_ATTEMPTS_PHASE1);
  });

  // Window load fallback if loader events bypassed
  window.addEventListener('load', () => {
    if (!window.__loaderHiddenFired && window.location.hash) {
      warn('LoaderHidden not fired before load; invoking scheduleInitial via load fallback');
      scheduleInitial();
    }
  });
})();
